/**
 * Recovery for the "stale chunk after deploy" failure mode.
 *
 * The app is code-split into content-hashed chunks (e.g. `FerryCheckInPage-DjAqYue7.js`) that are
 * lazy-loaded on navigation. When a deploy replaces the bundle while a tab is still open, the old
 * hashed filenames the running page remembers no longer exist on the server, so the next lazy import
 * 404s with "Failed to fetch dynamically imported module". A single reload pulls the fresh
 * (no-store) index.html and its new chunk names, resolving it.
 */

const RELOAD_TIMESTAMP_KEY = 'stale_chunk_reloaded_at';

// If a reload doesn't fix things within this window, the chunk is genuinely unreachable (not just
// a deploy) — stop reloading and let the error surface instead of trapping the user in a loop.
const RELOAD_LOOP_GUARD_MS = 10_000;

const STALE_CHUNK_MESSAGE_FRAGMENTS = [
	'failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'importing a module script failed',
	'unable to preload css', // Safari variant
];

/**
 * True if the error looks like a lazy-loaded chunk that went missing after a deploy.
 */
export const isStaleChunkError = (error: unknown): boolean => {
	let message = '';
	if (typeof error === 'string') {
		message = error;
	} else if (error instanceof Error) {
		message = error.message;
	} else if (error && typeof (error as { message?: unknown }).message === 'string') {
		message = (error as { message: string }).message;
	}

	const lower = message.toLowerCase();
	return STALE_CHUNK_MESSAGE_FRAGMENTS.some(fragment => lower.includes(fragment));
};

// True once THIS page load has triggered a reload. The same failed import is typically reported
// twice (vite:preloadError first, then the React ErrorBoundary); without this flag the second
// report lands inside the sessionStorage guard window, gets `false` back, and flashes the error UI
// while the reload is already in flight. The sessionStorage timestamp only guards across reloads.
let reloadInFlight = false;

/**
 * Reload the page once to pick up the freshly-deployed bundle. Returns `true` if a reload was
 * triggered (or is already in flight — keep rendering blank), or `false` if the previous page load
 * already reloaded within the loop-guard window and it didn't help (caller should then show the
 * error UI rather than reload again).
 */
export const reloadOnceForStaleChunk = (): boolean => {
	if (reloadInFlight) {
		return true;
	}

	const lastReloadAt = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0);

	if (Date.now() - lastReloadAt < RELOAD_LOOP_GUARD_MS) {
		return false;
	}

	reloadInFlight = true;
	sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
	window.location.reload();
	return true;
};
